/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Ortak asistan composer menüsü. GlobalVoiceAssistant ve ürün içi AI chat
 * aynı menü kabuğunu kullanır; aksiyonlar ilgili ekran tarafından sağlanır.
 */
import React from 'react';
import { Camera, Paperclip, Plus } from 'lucide-react';
import { Icon } from './ComponentIcon';
import './AssistantComposerMenu.css';

export interface AssistantComposerMenuItem {
  id: string;
  title: string;
  description?: string;
  section?: string;
  iconName?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  titleAttribute?: string;
  onSelect: () => void | Promise<void>;
}

interface AssistantComposerMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: AssistantComposerMenuItem[];
  label: string;
  disabled?: boolean;
  wrapperClassName?: string;
  buttonClassName?: string;
  activeButtonClassName?: string;
  menuClassName?: string;
  itemClassName?: string;
  iconSize?: number;
}

function joinClasses(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function renderComposerIcon(item: AssistantComposerMenuItem): React.ReactNode {
  if (item.icon) return item.icon;
  if (item.iconName === 'camera') return <Camera size={16} aria-hidden />;
  if (item.iconName === 'paperclip') return <Paperclip size={16} aria-hidden />;
  if (item.iconName) return <Icon name={item.iconName} size={16} />;
  return null;
}

export function AssistantComposerMenu({
  open,
  onOpenChange,
  items,
  label,
  disabled = false,
  wrapperClassName,
  buttonClassName,
  activeButtonClassName = 'active',
  menuClassName,
  itemClassName,
  iconSize = 18,
}: AssistantComposerMenuProps) {
  const handleSelect = (item: AssistantComposerMenuItem) => {
    if (item.disabled || disabled) return;
    onOpenChange(false);
    void item.onSelect();
  };

  return (
    <div className={joinClasses('assistant-composer-menu', wrapperClassName)}>
      <button
        type="button"
        className={joinClasses('assistant-composer-menu__button', buttonClassName, open && activeButtonClassName)}
        onClick={() => onOpenChange(!open)}
        title={label}
        aria-label={label}
        aria-expanded={open}
        disabled={disabled}
      >
        <Plus size={iconSize} aria-hidden />
      </button>
      {open ? (
        <div className={joinClasses('assistant-composer-menu__panel', menuClassName)} role="menu" aria-label={label}>
          {items.map((item, index) => {
            const icon = renderComposerIcon(item);
            return (
              <React.Fragment key={item.id}>
                {item.section && (index === 0 || items[index - 1]?.section !== item.section) ? (
                  <div className="assistant-composer-menu__section" role="presentation">
                    {item.section}
                  </div>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  className={joinClasses('assistant-composer-menu__item', itemClassName)}
                  onClick={() => handleSelect(item)}
                  disabled={item.disabled || disabled}
                  title={item.titleAttribute || item.title}
                >
                  {icon ? <span className="assistant-composer-menu__icon">{icon}</span> : null}
                  <span className="assistant-composer-menu__content">
                    <strong>{item.title}</strong>
                    {item.description ? <small>{item.description}</small> : null}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default AssistantComposerMenu;
